import { CanAccess, EditBase, Form, useRecordContext } from "ra-core";
import { Navigate } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";

import { FormToolbar } from "../layout/FormToolbar";
import type { IncentiveProgram } from "../types";
import { IncentiveProgramInputs } from "./IncentiveProgramInputs";
import {
  INCENTIVE_PROGRAM_PERIOD_CHOICES,
  INCENTIVE_PROGRAM_SCOPE_CHOICES,
} from "./incentiveProgramForm";
import { NewVigenciaDialog } from "./NewVigenciaDialog";

const IncentiveProgramEditForm = () => {
  const record = useRecordContext<IncentiveProgram>();
  if (!record) return null;

  return (
    <div className="mt-2 max-w-lg mx-auto flex flex-col gap-4">
      <Card>
        <CardContent>
          <Form className="space-y-4 pt-4">
            {/* `code`/`name`/`scope`/`period` are read-only: only the
                parameters and validity dates change on an existing version
                (ticket description) -- a new set of values is a NEW version,
                created via "Nueva vigencia" below, never an edit of `code`. */}
            <TextInput
              source="name"
              readOnly
              label="resources.incentive_programs.fields.name"
              helperText={false}
            />
            <SelectInput
              source="scope"
              choices={INCENTIVE_PROGRAM_SCOPE_CHOICES}
              disabled
              label="resources.incentive_programs.fields.scope"
              helperText={false}
            />
            <SelectInput
              source="period"
              choices={INCENTIVE_PROGRAM_PERIOD_CHOICES}
              disabled
              label="resources.incentive_programs.fields.period"
              helperText={false}
            />
            <IncentiveProgramInputs code={record.code} />
            <FormToolbar />
          </Form>
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <NewVigenciaDialog program={record} />
      </div>
    </div>
  );
};

/**
 * Almikia Epic E7 (TASK-095): the "Incentivos" edit page -- direccion edits
 * an EXISTING version's parameters and validity dates; ending a program
 * means setting `valid_to` here, never a delete (no delete action exists
 * anywhere on this screen, matching `canAccess.ts`'s
 * `incentive_programs`/"delete" -> false). Gated the same way as
 * `ProjectEdit`/`IncentiveProgramList` so a non-direccion role can't reach
 * an editable form by typing the URL directly.
 */
export const IncentiveProgramEdit = () => (
  <CanAccess
    resource="incentive_programs"
    action="edit"
    accessDenied={<Navigate to="/" replace />}
  >
    <EditBase actions={false} redirect="list">
      <IncentiveProgramEditForm />
    </EditBase>
  </CanAccess>
);
